import { get } from 'lodash-es';
import { message as EeveeMessage } from '@fuxi/eevee-ui';
import { AxiosError, AxiosResponse, HttpStatusCode } from 'axios';
import { ROUTER_PATHNAME } from 'common/router/config';
import { handleLogout, safeNav, safeToLogin, isCanceledError } from 'common/utils';
import {
  FastApiStatusCode,
  FastApiStatusMessage,
  FastApiErrorStatusCode,
  FastApiWarningStatusCode,
} from 'common/service/constants/fastApi';

const navigate = (pathname: string, next?: string) => {
  if (window.location.pathname !== pathname) {
    setTimeout(() => (window.location.href = `${pathname}${!!next ? `?next=${next}` : ''}`), 500);
  }
};

export const fastApiErrorHandler = (error: AxiosError) => {
  // @ts-ignore
  // Ignore error when request is canceled by user:
  if (isCanceledError(error)) return;

  const status: number = get(error, 'response.status', 0);
  const code: number = get(error, 'response.data.code', 0);
  const message = get(error, 'response.data.message') || get(error, 'response.data.msg', '服务端异常');

  // http状态码处理
  switch (status) {
    case HttpStatusCode.Unauthorized: {
      EeveeMessage.error(message);
      handleLogout();
      safeToLogin();
      break;
    }
    case HttpStatusCode.InternalServerError: {
      EeveeMessage.error(message);
      break;
    }
    default: {
      EeveeMessage.error(message);
      break;
    }
  }

  return Promise.reject({ status, code, message });
};

export const fastApiResponseHandler = async (response: AxiosResponse): Promise<ExtendAxioResponse> => {
  // 是否为下载场景 responseType:"blob"
  const responseType = get(response.request, 'responseType');

  // 默认情况下 json 结构对象，下载场景为 blob（即使内容是 json）
  let srcData = response.data;

  // 主要针对请求设定 responseType:"blob" 的场景，但报错返回 json 结果的场景
  if (srcData?.type === 'application/json') {
    const jsonString = await (srcData as Blob).text();
    srcData = JSON.parse(jsonString);
  }

  const code = get(srcData, 'code', responseType === 'blob' ? 0 : -1);
  const data = get(srcData, 'data', srcData);
  const message = get(srcData, 'message', FastApiStatusMessage?.[code] || '未知错误');
  const finalResponse = { ...response, data, code, message };

  FastApiErrorStatusCode.includes(code) && EeveeMessage.error(message);
  FastApiWarningStatusCode.includes(code) && EeveeMessage.warning(message);

  switch (code) {
    case FastApiStatusCode.Unauthorized:
      handleLogout();
      safeToLogin();
      return Promise.reject(finalResponse);
    case FastApiStatusCode.PhoneNotBound:
      navigate(`/${ROUTER_PATHNAME.LOGIN}/${ROUTER_PATHNAME.BIND_PHONE}`, window.location.pathname ?? '/');
      return Promise.reject(finalResponse);
    case FastApiStatusCode.ProtocolUpdate:
      navigate(`/${ROUTER_PATHNAME.LOGIN}/${ROUTER_PATHNAME.PROTOCOL_UPDATE}`, window.location.pathname ?? '/');
      return Promise.reject(finalResponse);
    case FastApiStatusCode.RoleSelection:
      navigate(`/${ROUTER_PATHNAME.LOGIN}/${ROUTER_PATHNAME.CHOOSE_ROLE}`, window.location.pathname ?? '/');
      return Promise.reject(finalResponse);
    case FastApiStatusCode.NoPermission:
      // 运营后台-制作方提现失败时错误返回了 NoPermission=10004，导致页面刷新，这里排除一下（后续可协调接口优化返回 code）
      if (!['manager', 'project-admin'].includes(import.meta.env.VITE_PLATFORM as string)){
        navigate(`/${ROUTER_PATHNAME.TASK}`);
      }
      return Promise.reject(finalResponse);
    case FastApiStatusCode.UserBlocked:
      navigate(`/${ROUTER_PATHNAME.LOGIN_ERROR}`);
      return Promise.reject(finalResponse);
    case FastApiStatusCode.NickNameNotBound:
      navigate(`/${ROUTER_PATHNAME.NICKNAME_SETTING}`);
      return Promise.reject(finalResponse);
    case FastApiStatusCode.PlatformUpdate:
      navigate(`/${ROUTER_PATHNAME.PLATFORM_UPDATE}`);
      return Promise.reject(finalResponse);
    default:
      break;
  }

  return FastApiErrorStatusCode.includes(code) ? Promise.reject(finalResponse) : finalResponse;
};
